import { supabase } from '../lib/supabase'
import { DashCLICompany, DashCLIAccessLog, HistoricoAcesso } from '../types/dashcli'

export const dashcliService = {
  // Listar empresas
  async listCompanies(): Promise<DashCLICompany[]> {
    const { data, error } = await supabase
      .from('dashcli_companies_integration')
      .select('*')
      .order('created_at', { ascending: false })
    
    if (error) throw error
    return (data || []) as DashCLICompany[]
  },

  // Criar ou atualizar empresa
  async upsertCompany(company: Partial<DashCLICompany>): Promise<DashCLICompany | null> {
    const { data, error } = await supabase
      .from('dashcli_companies_integration')
      .upsert(company, { onConflict: 'id' })
      .select('*')
      .single()
    
    if (error) throw error
    return data as DashCLICompany
  },

  // Excluir empresa
  async deleteCompany(id: string): Promise<void> {
    const { error } = await supabase
      .from('dashcli_companies_integration')
      .delete()
      .eq('id', id)
    
    if (error) throw error
  },

  // Buscar logs de acesso
  async getAccessLogs(filters?: {
    companyId?: string
    supergroupId?: string
    dateFrom?: string
    dateTo?: string
    status?: 'success' | 'failed'
  }): Promise<DashCLIAccessLog[]> {
    let query = supabase
      .from('dashcli_access_logs')
      .select('*')
      .order('access_timestamp', { ascending: false })

    if (filters?.companyId) {
      query = query.eq('company_id', filters.companyId)
    }

    if (filters?.supergroupId) {
      query = query.eq('supergroup_id', filters.supergroupId)
    }

    if (filters?.dateFrom) {
      query = query.gte('access_timestamp', filters.dateFrom)
    }

    if (filters?.dateTo) {
      query = query.lte('access_timestamp', filters.dateTo)
    }

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }

    const { data, error } = await query

    if (error) {
      console.error('Erro ao buscar logs de acesso:', error)
      throw error
    }
    
    console.log('Logs encontrados:', data?.length || 0, 'registros')
    return (data || []) as DashCLIAccessLog[]
  },

  // Buscar histórico de acessos SEM agrupar - mostra cada acesso individualmente
  async getHistoricoAcessos(filters?: {
    clienteQuery?: string
    cnpjQuery?: string
    status?: 'success' | 'failed'
    dateFrom?: string
    dateTo?: string
    companyCode?: string
    supergroupCode?: string
  }): Promise<HistoricoAcesso[]> {
    // Buscar logs com filtros aplicados
    const logs = await this.getAccessLogs({
      status: filters?.status,
      dateFrom: filters?.dateFrom,
      dateTo: filters?.dateTo,
      companyId: filters?.companyCode,
      supergroupId: filters?.supergroupCode
    })
    
    console.log('Total de logs retornados:', logs.length)
    console.log('Logs detalhados:', logs.map(l => ({
      id: l.id,
      company_id: l.company_id,
      supergroup_id: l.supergroup_id,
      access_timestamp: l.access_timestamp,
      status: l.status
    })))

    // Buscar códigos únicos de company_id e supergroup_id
    const companyCodes = Array.from(new Set(logs.map(l => l.company_id).filter(Boolean) as string[]))
    const supergroupCodes = Array.from(new Set(logs.map(l => l.supergroup_id).filter(Boolean) as string[]))
    
    console.log('Company codes únicos:', companyCodes)
    console.log('Supergroup codes únicos:', supergroupCodes)

    // Buscar empresas da tabela dashcli_companies_integration
    // Se company_id estiver preenchido, buscar onde code = company_id e type = 'codigo_empresa'
    const companiesMap = new Map<string, { name: string; cnpj?: string }>()
    if (companyCodes.length > 0) {
      try {
        const { data: companies, error } = await supabase
          .from('dashcli_companies_integration')
          .select('code, company_name')
          .in('code', companyCodes)
          .eq('type', 'codigo_empresa')

        if (error) {
          console.error('Erro ao buscar empresas:', error)
        } else if (companies) {
          console.log('Empresas encontradas:', companies)
          companies.forEach((c: any) => {
            companiesMap.set(c.code, { name: c.company_name || c.code, cnpj: '' })
          })
          console.log('CompaniesMap:', Array.from(companiesMap.entries()))
        }
      } catch (error) {
        console.warn('Erro ao buscar empresas:', error)
      }
    }

    // Buscar supergrupos da tabela dashcli_companies_integration
    // Se supergroup_id estiver preenchido, buscar onde code = supergroup_id e type = 'supergrupo'
    const supergroupsMap = new Map<string, { name: string; cnpj?: string }>()
    if (supergroupCodes.length > 0) {
      try {
        const { data: supergroups, error } = await supabase
          .from('dashcli_companies_integration')
          .select('code, company_name')
          .in('code', supergroupCodes)
          .eq('type', 'supergrupo')

        if (error) {
          console.error('Erro ao buscar supergrupos:', error)
        } else if (supergroups) {
          console.log('Supergrupos encontrados:', supergroups)
          supergroups.forEach((s: any) => {
            supergroupsMap.set(s.code, { name: s.company_name || s.code, cnpj: '' })
          })
          console.log('SupergroupsMap:', Array.from(supergroupsMap.entries()))
        }
      } catch (error) {
        console.warn('Erro ao buscar supergrupos:', error)
      }
    }

    // Criar um registro para cada acesso individual (SEM agrupar)
    let historico: HistoricoAcesso[] = logs.map(log => {
      // Buscar informações baseado no tipo: company_id -> codigo_empresa, supergroup_id -> supergrupo
      const info = log.company_id 
        ? companiesMap.get(log.company_id) 
        : log.supergroup_id
        ? supergroupsMap.get(log.supergroup_id)
        : null

      // Usar company_name encontrado ou mostrar em branco se não encontrar
      const cliente = info?.name || ''
      const cnpj = info?.cnpj || ''
      
      // Log para debug
      if (log.company_id || log.supergroup_id) {
        console.log(`Log ${log.id}: company_id=${log.company_id}, supergroup_id=${log.supergroup_id}, cliente encontrado=${cliente || '(vazio)'}`)
      }

      return {
        id: log.id,
        cliente,
        cnpj,
        primeiroAcesso: log.access_timestamp,
        ultimoAcesso: log.access_timestamp,
        company_id: log.company_id,
        supergroup_id: log.supergroup_id,
        route: log.route,
        status: log.status,
        ip_address: log.ip_address,
        user_agent: log.user_agent,
        error_message: log.error_message,
        created_at: log.created_at,
        access_timestamp: log.access_timestamp
      }
    })
    
    console.log('Total de registros no histórico (sem agrupar):', historico.length)

    // Aplicar filtros de texto (cliente e CNPJ) após buscar os dados
    if (filters?.clienteQuery) {
      const query = filters.clienteQuery.toLowerCase()
      historico = historico.filter(h => h.cliente.toLowerCase().includes(query))
    }

    if (filters?.cnpjQuery) {
      const query = filters.cnpjQuery.toLowerCase()
      historico = historico.filter(h => h.cnpj.toLowerCase().includes(query))
    }

    const resultado = historico.sort((a, b) => 
      new Date(b.ultimoAcesso).getTime() - new Date(a.ultimoAcesso).getTime()
    )
    
    console.log('Resultado final:', resultado.length, 'registros no histórico')
    return resultado
  },

  // Contar total de acessos no histórico (todos os status)
  async getTotalAcessos(): Promise<number> {
    const { count, error } = await supabase
      .from('dashcli_access_logs')
      .select('*', { count: 'exact', head: true })

    if (error) throw error
    return count || 0
  },

  // Contar acessos de hoje (todos os status)
  async getAcessosHoje(): Promise<number> {
    const hoje = new Date()
    hoje.setHours(0, 0, 0, 0)
    const hojeStr = hoje.toISOString()

    const amanha = new Date(hoje)
    amanha.setDate(amanha.getDate() + 1)
    const amanhaStr = amanha.toISOString()

    const { count, error } = await supabase
      .from('dashcli_access_logs')
      .select('*', { count: 'exact', head: true })
      .gte('access_timestamp', hojeStr)
      .lt('access_timestamp', amanhaStr)

    if (error) throw error
    return count || 0
  },
}
